// Admin-controlled share URL host override. Stored in localStorage so it
// persists across reloads but is purely client-side.
//
// Selection model:
//   - 'auto' (default for everyone, no localStorage key): resolves at call
//     time from window.location.hostname.
//   - 'live' | 'lovable' | 'dev': forced override, persisted in localStorage.

export type ShareHostId = 'live' | 'lovable' | 'dev';
export type ShareHostSelection = 'auto' | ShareHostId;

const STORAGE_KEY = 'share-host-override';

export interface ShareHostOption {
  id: ShareHostId;
  label: string;
  description: string;
  url: string;
}

export const SHARE_HOSTS: ShareHostOption[] = [
  {
    id: 'live',
    label: 'Live (chopperswap.com)',
    description: 'Production custom domain — required for real social sharing.',
    url: 'https://www.chopperswap.com',
  },
  {
    id: 'lovable',
    label: 'Lovable Published',
    description: 'Stable lovable.app published URL — works even without custom domain.',
    url: 'https://chopperswap.lovable.app',
  },
  {
    id: 'dev',
    label: 'Dev (chopper.flutschi.ch)',
    description: 'Personal dev domain used for staging tests.',
    url: 'https://chopper.flutschi.ch',
  },
];

// hostname → host id mapping for autodetect.
const HOSTNAME_TO_ID: Record<string, ShareHostId> = {
  'www.chopperswap.com': 'live',
  'chopperswap.com': 'live',
  'chopperswap.lovable.app': 'lovable',
  'chopper.flutschi.ch': 'dev',
};

const FALLBACK_ID: ShareHostId = 'lovable';

const getHostUrl = (id: ShareHostId): string =>
  SHARE_HOSTS.find((h) => h.id === id)?.url ?? SHARE_HOSTS[0].url;

export const detectShareHostId = (): ShareHostId => {
  if (typeof window === 'undefined') return FALLBACK_ID;
  const host = window.location?.hostname ?? '';
  return HOSTNAME_TO_ID[host] ?? FALLBACK_ID;
};

/** Returns the raw user selection: 'auto' if no override, else the forced id. */
export const getShareHostSelection = (): ShareHostSelection => {
  if (typeof window === 'undefined') return 'auto';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as ShareHostId | null;
    if (stored && SHARE_HOSTS.some((h) => h.id === stored)) return stored;
  } catch {
    // ignore (private mode, etc.)
  }
  return 'auto';
};

/** Returns the resolved host id (auto → detected). */
export const getShareHostId = (): ShareHostId => {
  const sel = getShareHostSelection();
  return sel === 'auto' ? detectShareHostId() : sel;
};

export const getShareHost = (): string => getHostUrl(getShareHostId());

export const setShareHost = (selection: ShareHostSelection): void => {
  if (typeof window === 'undefined') return;
  try {
    if (selection === 'auto') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, selection);
    }
  } catch {
    // ignore
  }
};

export const clearShareHostOverride = (): void => setShareHost('auto');
